import { Core, IPlugin } from 'trtc-js-sdk-core/src/plugin';
import { AudioCodecPipelineType, AudioDecodeProcessor, AudioDecodeProcessorParam, AudioDecoderDowngradeState, BASIC_TYPE, RemoteAudioTrack, ValidateRule } from 'trtc-js-sdk-core';
import { FSM } from 'afsm';
import {
  catchError,
  startWith,
  empty,
  fromEvent,
  ISink,
  Observable,
  pipe,
  share,
  Subject,
  subject,
  subscribe,
  switchMap,
  take,
  takeUntil
} from 'fastrx';
import { KV_REPORT_KEY_AUDIO_DECODE } from 'trtc-js-sdk-core/src/manager/kv-stat-manager';
// import { AudioDecoderSoft, AudioDecoderHard } from 'jv4-decoder';
// import { AudioDecoderEvent } from 'jv4-decoder/src/types';

export interface StartOption {
  track: RemoteAudioTrack;
  config: AudioDecoderConfig;
  type: 'wasm' | 'webCodecs' | 'mse' | 'auto';
}

interface UpdateOption extends Omit<StartOption, 'type'> {
  userId?: string;
  type: 'wasm' | 'webCodecs' | 'mse' | 'mock' | 'auto';
}

const enum DECODE_FAILED_ERROR_CODE {
  SUCCESS = 0,
  FAILED = 1,
  WEBCODEC_INIT = 2,
  WEBCODEC_CONFIG_NOT_SUPPORT = 3,
  WEBCODEC_DECODER_ERROR = 4,
  WEBCODEC_TRACK_MUTE = 5,
  WASM_INIT = 6,
  WASM_CONFIG_NOT_SUPPORT = 7,
  WASM_DECODER_ERROR = 8,
  WASM_TRACK_MUTE = 9,
  TEST = 10,
}

const getKvKey = (decoderType: string): KV_REPORT_KEY_AUDIO_DECODE => {
  switch (decoderType) {
    case 'webCodecs':
      return KV_REPORT_KEY_AUDIO_DECODE.DOWNGRADE_WEBCODECS;
    case 'wasm':
      return KV_REPORT_KEY_AUDIO_DECODE.DOWNGRADE_WASM;
  }
  throw new Error('decoder type not supported');
};
class DecodeContext {
  trackDoneOB: Observable<string>;
  startOB: Subject<number>;
  stopOB: Subject<any>;
  inputFrameCount = 0;
  decodedFrameCount = 0;
  type: 'auto' | 'wasm' | 'webCodecs' | 'mse' = 'auto';
  config: AudioDecoderConfig;
  // decoder?: AudioDecoderSoft | AudioDecoderHard;
  decoder?: AudioDecoder;
  private _decodeSink: ISink<AudioData>;
  constructor(core: Core, track: RemoteAudioTrack, option: StartOption) {
    const { kvStatManager, trtc } = core;
    this.config = option.config;
    this.trackDoneOB = fromEvent(track, FSM.INIT);
    this.stopOB = subject();
    this.startOB = subject();
    if (option.type === 'auto') {
      this.type = 'webCodecs';
    } else this.type = option.type;
    const stateChangeOB = subject();
    // let reason: string;
    // 状态变化逻辑 TODO: 后续考虑抛出事件
    // pipe(stateChangeOB, scan<AudioDecoderDowngradeState, AudioDecoderDowngradeState, AudioDecoderDowngradeState>((prevState, state) => {
    //   if (prevState !== state) trtc.emit(TRTCEvent.AUDIO_DECODE_DOWNGRADE_STATE_CHANGED, {
    //     userId: track.userId,
    //     reason,
    //     prevState,
    //     state
    //   });
    //   return state;
    // }, AudioDecoderDowngradeState.INITIALIZED), takeUntil(this.stopOB), subscribe(console.warn));

    // 检查降级逻辑：抛错则触发降级
    const checkDowngrade = (count: number) => {
      const decoderOB = this.pipe(track);
      stateChangeOB.next(AudioDecoderDowngradeState.STARTING);
      track.log.info(`decoder type: ${this.type}`);
      pipe(decoderOB, takeUntil(this.stopOB), subscribe(() => {
      }, err => {
        track.log.error(err);
        kvStatManager.addFailedEvent({ key: getKvKey(this.type), error: err });
        if (count > 4) this.startOB.error(err); // 最终失败
        else {
          // 重试/降级 TODO: wasm decoder 降级
          // switch (this.type) {
          //   case 'webCodecs':
          //     this.type = 'wasm';
          //     break;
          // }
          this.startOB.next(count + 1);
        }
      }));
      return pipe(decoderOB, take(1), catchError(empty));
    };

    pipe(this.startOB, startWith(0), switchMap(checkDowngrade), takeUntil(this.stopOB), subscribe(() => { // 成功解码第一帧
      track.player.setOutput();
      stateChangeOB.next(AudioDecoderDowngradeState.STARTED);
    }, err => { // 解码失败
      // reason = err;
      stateChangeOB.next(AudioDecoderDowngradeState.FAILED);
      // TODO: 后续考虑抛出事件
      // trtc.emit(TRTCEvent.ERROR, new RtcError({
      //   code: ErrorCode.OPERATION_FAILED,
      //   extraCode: ErrorCodeDictionary.AUDIO_DECODE_ERROR
      // }));
    }, () => { // 解码完成
      kvStatManager.addSuccessEvent({ key: getKvKey(this.type) });
      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_AUDIO_DECODE.DOWNGRADE_RESULT });
    }));
  }
  mock(err: DECODE_FAILED_ERROR_CODE) {
    if (this._decodeSink) this._decodeSink.error(err);
    else this.startOB.next(0);
  }
  close(reason: string) {
    this.stopOB.next(reason);
  }
  private pipe(track: RemoteAudioTrack) {
    return share()(async sink => {
      this._decodeSink = sink;
      sink.defer(() => { // 取消订阅
        this.decoder?.close();
      });
      const { type } = this;
      try {
        switch (type) {
          case 'webCodecs':
            // this.decoder = new AudioDecoderHard();
            this.decoder = new AudioDecoder({
              error: (err: DOMException) => {
                track.log.error(err);
                sink.error(DECODE_FAILED_ERROR_CODE.WEBCODEC_DECODER_ERROR);
              },
              output: (data: AudioData) => { // 解码
                this.decodedFrameCount++;
                sink.next(data);
                track.player.write(data);
              }
            });
            break;
          case 'wasm':
            // this.decoder = new AudioDecoderSoft();
            break;
        }
        // this.decoder!.on(AudioDecoderEvent.Error, (err: AudioDecoderEvent) => {
        //   track.log.error(err);
        //   sink.error(type === 'webCodecs' ? DECODE_FAILED_ERROR_CODE.WEBCODEC_DECODER_ERROR : DECODE_FAILED_ERROR_CODE.WASM_DECODER_ERROR);
        // });
        // this.decoder!.on(AudioDecoderEvent.AudioFrame, (frame: AudioData) => {
        //   this.decodedFrameCount++;
        //   sink.next(frame);
        //   track.player.write(frame);
        // });
        // await this.decoder!.initialize();
        this.decoder!.configure(this.config);
      } catch (err) {
        track.log.error(err);
        sink.error(type === 'webCodecs' ? DECODE_FAILED_ERROR_CODE.WEBCODEC_INIT : DECODE_FAILED_ERROR_CODE.WASM_INIT);
      }
    });
  }
  decodeFrame(frame: RTCEncodedAudioFrame) {
    this.inputFrameCount++;
    // if (this.decoder?.state === 'configured') this.decoder.decode({
    //   data: frame.data,
    //   timestamp: frame.timestamp,
    //   type: 'key'
    // });
    if (this.decoder?.state === 'configured') {
      this.decoder.decode(new EncodedAudioChunk({
        data: frame.data,
        timestamp: frame.timestamp,
        type: 'key'
      }));
    }
    return;
  }
}

const rule: ValidateRule = {
  type: BASIC_TYPE.Object
};

export class AudioDecoderPlugin implements IPlugin {
  static readonly Name = 'TRTCAudioDecoder';
  private log: Core['log'];
  private contextMap = new Map<RemoteAudioTrack, DecodeContext>();
  private decodeProcessorMap = new WeakMap<RemoteAudioTrack, AudioDecodeProcessor>();
  constructor(public core: Core) {
    this.log = core.log.createChild({ id: `${this.getAlias()}` });
  }

  getAlias(): string {
    return AudioDecoderPlugin.Name;
  }

  getGroup(option: StartOption): string {
    return option.track.userId + option.track.streamType;
  }

  getName(): string {
    return AudioDecoderPlugin.Name;
  }

  getValidateRule(method: 'start' | 'update' | 'stop'): ValidateRule | ValidateRule[] {
    return rule;
  }

  start(option: StartOption): void {
    const { track } = option;
    this.decodeProcessorMap.set(track, this.decode(option));
    this.core.room.audioManager.addDecodeProcessor({
      processor: ({ frame, track }) => {
        if (!this.decodeProcessorMap.has(track)) return frame;
        return this.decodeProcessorMap.get(track)!({ frame, track });
      },
      type: AudioCodecPipelineType.ENCODE_AND_DECODE
    });
  }
  // TODO: RTCRtpScriptTransform
  decode(option: StartOption) {
    return ({ frame, track }: AudioDecodeProcessorParam) => {
      if (track !== option.track) return frame;
      if (this.contextMap.has(track)) return this.contextMap.get(track)!.decodeFrame(frame);
      const context = new DecodeContext(this.core, track, option);
      // 如果 track 取消订阅或者 track 被删除，则删除 context
      pipe(context.trackDoneOB, take(1), subscribe(() => {
        this.core.clearStarted(this, this.getGroup(option));
        this.stop({ track });
      }));
      this.contextMap.set(track, context);
      return context.decodeFrame(frame);
    };
  }
  stop({ track }: { track: RemoteAudioTrack; }): void {
    const context = this.contextMap.get(track);
    if (!context) return;
    context.close('stop');
    this.contextMap.delete(track);
    if (this.contextMap.size === 0) this.core.room.audioManager.removeDecodeProcessor({ type: AudioCodecPipelineType.ENCODE_AND_DECODE });
  }

  update(option: UpdateOption): void {
    const context = this.contextMap.get(option.track);
    if (!context) return;
    if (option.type === 'mock') {
      context.mock(DECODE_FAILED_ERROR_CODE.TEST);
      return;
    }
    context.close('update');
    this.contextMap.set(option.track, new DecodeContext(this.core, option.track, option as StartOption));
  }
}
